// Shared permission helpers (SPEC-058). One place that answers "may the CURRENT user do X, and
// if not, why?" so screens render the same three states everywhere (SPEC-022/029 doctrine —
// everyone can SEE, only the qualified can ACT):
//
//   { allowed: true }                       → an active control
//   { allowed: false, tooltipKey: "…" }     → a GHOSTED control (disabled + translated "why")
//   { allowed: false, tooltipKey: null }    → no control at all (the actor has no such power)
//
// Every helper is a MIRROR of a server rule and the server remains the law (SPEC-051/056 pattern:
// the FE filter is cosmetics, a refused call is toasted from the message layer). None of them
// consults gmpControlsRelaxed: SPEC-010's dev relax is about SIGNING a record you would otherwise
// be excluded from, never about who may appoint, author or administer whom.
import { QA, QA_MANAGER, hasQcCapability, selectIsLimsQaManager, archiveDecision } from "./lims/limsCapabilities"

// The archive matrix (SPEC-057 L1) lives with the capability helpers it is built on; it is the
// helper this module generalises, so it is re-exported here as part of the same family.
export { archiveDecision }

export function selectIsPlanner(state) {
  return state.auth.role === "planner"
}

// The Setup area (team, instruments, analyses, …) is the planner's operational surface. Since
// SPEC-058 a qa-manager holder with the MEMBER planning role no longer enters it at all — the
// quality column has its own page under LIMS › QA team — so this is once again simply "not a
// member" (planners; the admin roles have their own screens and never reach the team Setup).
export function selectCanOpenSetup(state) {
  return !state.auth.isMember
}

// LIMS › QA team: visible to the head of the quality column (who acts there) and to the planner
// (who sees who holds QA, read-only). Never to anyone else, never on a Standard team.
export function selectCanViewQaTeam(state) {
  const showLims = !!(state.teamStatus && state.teamStatus.showLims)
  return showLims && (selectIsPlanner(state) || selectIsLimsQaManager(state))
}

export function selectCanManageQaTeam(state) {
  return selectIsLimsQaManager(state)
}

// SPEC-059: authoring LIMS master data (a new forskrift / template version, the comment
// library) is the PLANNER role only — approving stays a qualification (reviewer / supervisor).
export function selectCanAuthorMasterData(state) {
  return selectIsPlanner(state)
}

// May the actor grant / revoke the `qa` capability on `target`? Mirror of the backend matrix in
// `_authorize_capability_diff`: only a qa-manager holder touches `qa`, and never on themselves
// (the self-ban covers grant AND revoke). A qa-manager-bearing target may still receive `qa` —
// the diff touches only `qa`, which is allowed; `qa-manager` itself is the customer admin's.
export function qaRoleDecision({ actorIsQaManager, actorMemberId, target }) {
  if (!actorIsQaManager) {
    return { allowed: false, tooltipKey: null }
  }
  if (target.id === actorMemberId) {
    return { allowed: false, tooltipKey: "limsQaTeamSelfNote" }
  }
  return { allowed: true, tooltipKey: null }
}

// May the actor send the password (welcome / reset) mail to `target`? Mirror of
// `member.send_password_mail` (SPEC-057 L2 + SPEC-059): a planner for anyone on the team but
// themselves (self-service exists); a qa-manager holder for a `qa`-bearer on their own team —
// never a qa-manager-bearer, never themselves.
export function passwordMailDecision({ actorRole, actorIsQaManager, actorMemberId, target }) {
  if (target.id === actorMemberId) {
    return { allowed: false, tooltipKey: null }
  }
  if (actorRole === "planner") {
    return { allowed: true, tooltipKey: null }
  }
  const caps = new Set(target.qcCapabilities || [])
  if (actorIsQaManager && caps.has(QA) && !caps.has(QA_MANAGER)) {
    return { allowed: true, tooltipKey: null }
  }
  return { allowed: false, tooltipKey: null }
}

// Convenience for screens that need the raw capability flag of the current user.
export function selectHoldsCapability(state, capability) {
  return hasQcCapability(state, state.auth.memberId, capability)
}
